package de.oevbs.kunde.kundenportal2.model.services.mydata

import de.oevbs.kunde.kundenportal2.validation.KupoValidationMessages
import dev.fritz2.headless.validation.ComponentValidationMessage
import dev.fritz2.headless.validation.errorMessage
import dev.fritz2.validation.Validation
import dev.fritz2.validation.validation
import kotlin.jvm.JvmInline

/**
 * Die Iban Klasse wird benötigt, um die Nutzereingaben zu validieren.
 * Es werden Basisvalidierungen und auch Format- und Ländercode-Prüfungen durchgeführt.
 */
@JvmInline
value class Iban(val value: String) {

    companion object {
        private val ibanCountryCodeRegex = "^[a-zA-Z]{2}.+$".toRegex()

        // Anzahl der Zeichen von IBANs verschiedener Länder (nach Ländercode).
        // Siehe: https://en.wikipedia.org/wiki/International_Bank_Account_Number#IBAN_formats_by_country
        private val ibanLengthToCountryCode = mapOf(
            "AL" to 28, "AD" to 24, "AT" to 20, "AZ" to 28, "BH" to 22, "BY" to 28, "BE" to 16, "BA" to 20, "BR" to 29,
            "BG" to 22, "CR" to 22, "HR" to 21, "CY" to 28, "CZ" to 24, "DK" to 18, "DO" to 28, "TL" to 23, "EG" to 29,
            "SV" to 28, "EE" to 20, "FO" to 18, "FI" to 18, "FR" to 27, "GE" to 22, "DE" to 22, "GI" to 23, "GR" to 27,
            "GL" to 18, "GT" to 28, "HU" to 28, "IS" to 26, "IQ" to 23, "IE" to 22, "IL" to 23, "IT" to 27, "JO" to 30,
            "KZ" to 20, "XK" to 20, "KW" to 30, "LV" to 21, "LB" to 28, "LY" to 25, "LI" to 21, "LT" to 20, "LU" to 20,
            "MK" to 19, "MT" to 31, "MR" to 27, "MU" to 30, "MC" to 27, "MD" to 24, "ME" to 22, "NL" to 18, "NO" to 15,
            "PK" to 24, "PS" to 29, "PL" to 28, "PT" to 25, "QA" to 29, "RO" to 24, "LC" to 32, "SM" to 27, "ST" to 25,
            "SA" to 24, "RS" to 22, "SC" to 31, "SK" to 24, "SI" to 19, "ES" to 24, "SD" to 18, "SE" to 24, "CH" to 21,
            "TN" to 24, "TR" to 26, "UA" to 29, "AE" to 23, "GB" to 22, "VA" to 22, "VG" to 24
        )

        // IBAN sollte prinzipiell im Store von illegalen Zeichen bereinigt werden, nicht aber in der
        // Validierung! Grundsätzlich sollte es eine `Iban.sanitize()`-Funktion geben, die alle illegalen Zeichen
        // aus einer Iban entfernt und innerhalb der Formats.iban Lens aufgerufen wird. Somit würde der jeweilige Store
        // immer valide Daten enthaltend und die Validation müsste nicht selber Zeichen entfernen.
        // Aus Gründen, die im fritz2-Issue https://github.com/jwstegemann/fritz2/issues/858 näher erläutert werden,
        // ist dies aktuell jedoch nicht möglich.
        //
        // Bspw.:
        // private val validChars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ".toCharArray().toSet()
        // fun sanitize(rawIban: String): String = rawIban.uppercase().filter { validChars.contains(it) }
        //
        // FIXME: Umbauen, sobald fritz2 Issue #858 umgesetzt ist
        val validation: Validation<String, Unit, ComponentValidationMessage> = validation { inspector ->
            if (inspector.data.isEmpty()) {
                add(inspector.errorMessage(KupoValidationMessages.EMPTY_INPUT))
                return@validation
            }

            val iban = inspector.data.replace(" ", "")

            //FIXME: Solange die internationalen IBANs im BP noch nicht umgesetzt sind, ist die Prüfung auf
            // den deutschen Countrycode notwendig
            if (!iban.startsWith("DE", true)) {
                add(inspector.errorMessage("Bitte geben Sie eine deutsche IBAN ein."))
                return@validation
            }

            if (!ibanCountryCodeRegex.matches(iban)) {
                add(inspector.errorMessage("Die IBAN muss mit einem Ländercode beginnen."))
                return@validation
            }

            val countryCode = iban.take(2)
            if (ibanLengthToCountryCode.containsKey(countryCode) && iban.length != ibanLengthToCountryCode[countryCode]) {
                add(inspector.errorMessage("Die IBAN muss aus ${ibanLengthToCountryCode[countryCode]} Zeichen bestehen."))
            } else if (!ibanLengthToCountryCode.containsKey(countryCode.uppercase())) {
                add(inspector.errorMessage("Die IBAN beginnt mit einem unbekannten Ländercode."))
            }
        }
    }
}